import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { useEhiOptions } from '@hooks/useEhiOptions.ts';
import { ehiService } from '@services/ehi.service.ts';

interface Props {
  /** Id користувача; null - модалка закрита. */
  userId: number | null;
  onClose: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
  </>
);

// Деталі розрахунку узагальнених даних одного користувача: чому саме така цифра і з чого вона складається -
// збережений запис, вікно 30 днів, сирі знімки (вхід) і перерахунок зараз за поточною формулою (для налагодження).
export const EhiSummaryDetailsModal: React.FC<Props> = ({ userId, onClose }) => {
  const { t } = useTranslation();
  const { levels } = useEhiOptions();
  const levelLabel = (v: string) => levels.find((o) => o.value === v)?.label ?? v;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ehi', 'summary-details', userId],
    queryFn: () => ehiService.getSummaryDetails(userId!),
    enabled: userId !== null,
  });

  const stored = data?.summary;
  const recomputed = data?.recomputed;
  const matches = stored && recomputed ? stored.ehiValueDisplay === recomputed.ehiValueDisplay && stored.ehiLevel === recomputed.ehiLevel : null;

  return (
    <BaseDialog
      open={userId !== null}
      onClose={onClose}
      title={t('ehiJournal.summaryDetailsTitle')}
      iDoc={ModalIndices.ADMIN_EHI_SUMMARY_DETAILS}
      maxWidth="md"
      actions={<Button onClick={onClose}>{t('ehiJournal.close')}</Button>}
    >
      {isLoading && <Box display="flex" justifyContent="center" sx={{ py: 3 }}><CircularProgress /></Box>}
      {isError && <Alert severity="error">{t('ehiJournal.detailsError')}</Alert>}
      {stored && data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>{stored.userName ?? stored.userEmail ?? `#${stored.userId}`}</Typography>
            {stored.userName && <Typography variant="caption" color="text.secondary">{stored.userEmail}</Typography>}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionStored')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
              <Row label={t('ehiJournal.colEhi')} value={`${stored.ehiValueDisplay} (${stored.ehiValueRaw})`} />
              <Row label={t('ehiJournal.colLevel')} value={levelLabel(stored.ehiLevel)} />
              <Row label={t('ehiJournal.colDays')} value={`${stored.closedDaysCount}/30`} />
              <Row label={t('ehiJournal.colUpdated')} value={new Date(stored.updatedAt).toLocaleString()} />
            </Box>
          </Box>

          {/* 6 напрямків - НЕРОЗРИВНА складова EHI (рішення користувача 2026-09-23), не опційний додаток. */}
          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionDirections')}</Typography>
            {stored.directionScores ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
                <Row label={t('ehiJournal.directionPhysical')} value={Math.round(stored.directionScores.physical)} />
                <Row label={t('ehiJournal.directionEnergy')} value={Math.round(stored.directionScores.energy)} />
                <Row label={t('ehiJournal.directionClarity')} value={Math.round(stored.directionScores.clarity)} />
                <Row label={t('ehiJournal.directionProductivity')} value={Math.round(stored.directionScores.productivity)} />
                <Row label={t('ehiJournal.directionFocus')} value={Math.round(stored.directionScores.focus)} />
                <Row label={t('ehiJournal.directionEmotional')} value={Math.round(stored.directionScores.emotional)} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noDirections')}</Typography>
            )}
          </Box>

          {/* "Чому саме таке число" на рівні блоків (рішення користувача 2026-09-23: "побачити хочу, як воно
              розраховувалося") - середній бал по РЕАЛЬНИХ знімках вікна (не forward-fill), не лише 6 напрямків. */}
          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionBlockAverages')}</Typography>
            {data.blockAverages ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
                <Row label={t('ehiJournal.blockWater')} value={data.blockAverages.water ?? '—'} />
                <Row label={t('ehiJournal.blockNutrition')} value={data.blockAverages.nutrition ?? '—'} />
                <Row label={t('ehiJournal.blockActivity')} value={data.blockAverages.activity ?? '—'} />
                <Row label={t('ehiJournal.blockFeeling')} value={data.blockAverages.feeling ?? '—'} />
                <Row label={t('ehiJournal.blockTasks')} value={data.blockAverages.tasks ?? '—'} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noBlockAverages')}</Typography>
            )}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionWindow')}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>{data.window.dateFrom} — {data.window.dateTo}</Typography>
            {data.snapshots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noWindowSnapshots')}</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('ehiJournal.colDate')}</TableCell>
                    <TableCell align="right">{t('ehiJournal.colEhi')}</TableCell>
                    <TableCell>{t('ehiJournal.colLevel')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.snapshots.map((s) => (
                    <TableRow key={s.snapshotDate}>
                      <TableCell>{s.snapshotDate}</TableCell>
                      <TableCell align="right">{s.ehiValueDisplay} ({s.ehiValueRaw})</TableCell>
                      <TableCell>{levelLabel(s.ehiLevel)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionRecomputed')}</Typography>
            {recomputed ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
                  <Row label={t('ehiJournal.colEhi')} value={`${recomputed.ehiValueDisplay} (${recomputed.ehiValueRaw})`} />
                  <Row label={t('ehiJournal.colLevel')} value={levelLabel(recomputed.ehiLevel)} />
                  <Row label={t('ehiJournal.colDays')} value={`${recomputed.daysUsed}/30`} />
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }} color="text.secondary">{recomputed.explanation}</Typography>
                <Chip size="small" sx={{ mt: 1 }} color={matches ? 'success' : 'warning'} label={matches ? t('ehiJournal.matches') : t('ehiJournal.differs')} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noRecompute')}</Typography>
            )}
          </Box>
        </Box>
      )}
    </BaseDialog>
  );
};
